import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import MenuCard from "@/components/MenuCard";
import BroadcastBanner from "@/components/BroadcastBanner";
import { fetchMenu, fetchStatus, fetchSlots } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, MapPin, Clock, Phone, Instagram, Facebook } from "lucide-react";

const STATUS_POLL_MS = 30000;

export default function Home() {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ orders_closed: false, closed_message: "" });
  const [todaySlots, setTodaySlots] = useState(null); // null = loading, [] = closed, [...] = open
  const { totals, setIsOpen } = useCart();

  useEffect(() => {
    fetchMenu().then(items => { setMenu(items); setLoading(false); });
    fetchStatus().then(setStatus).catch(() => {});
    const todayIso = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD in local tz
    fetchSlots(todayIso).then(setTodaySlots).catch(() => setTodaySlots([]));
    const iv = setInterval(() => {
      fetchStatus().then(setStatus).catch(() => {});
      fetchSlots(todayIso).then(setTodaySlots).catch(() => {});
    }, STATUS_POLL_MS);
    return () => clearInterval(iv);
  }, []);

  const pickupHoursText = useMemo(() => {
    if (todaySlots === null) return "Loading today's hours…";
    if (!todaySlots.length) return "Closed today · See other dates at checkout";
    const first = todaySlots[0];
    const last = todaySlots[todaySlots.length - 1];
    return `Today · ${first} – ${last} · 30-min slots`;
  }, [todaySlots]);

  const grouped = useMemo(() => {
    const g = {};
    menu.forEach(m => {
      const cat = m.category || "Menu";
      g[cat] = g[cat] || [];
      g[cat].push(m);
    });
    const order = ["Plates", "Kelaguen", "Spam Musubi", "Lumpia", "Sides", "Drinks"];
    return order.filter(k => g[k]).map(k => ({ category: k, items: g[k] }));
  }, [menu]);

  return (
    <div className="min-h-screen island-pattern">
      {status.orders_closed && <BroadcastBanner message={status.closed_message} />}
      <Navbar />

      {/* Hero */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grain" aria-hidden />
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1525923838299-2312b60f6d69?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-14 sm:pt-20 sm:pb-24">
          <span className="chip" data-testid="hero-chip">Chamoru BBQ · Pre-order for pickup</span>
          <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#2C241B] leading-[1.02] tracking-tight max-w-3xl">
            Håfa Adai! <br />
            <span className="text-[#D9381E]">Chamoru BBQ plates,</span> straight from the grill.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#2C241B]/80 max-w-xl">
            Marinated BBQ ribs, red rice, potato salad, and pickles — plus hand-rolled lumpia. Pre-order online and pick up warm.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="#menu" data-testid="hero-order-now-btn" className="cta-primary rounded-full px-7 py-3.5 font-display font-extrabold text-base inline-flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" /> Order Now
            </a>
            <a href="#info" data-testid="hero-learn-more-btn" className="rounded-full px-7 py-3.5 font-semibold text-sm bg-white border border-[#E5D9C5] text-[#2C241B] hover:bg-[#F5EEE0] transition-colors inline-flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Pickup Info
            </a>
          </div>
        </div>
      </section>

      {/* Menu grouped by category */}
      <section id="menu" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-14">
        {loading ? (
          <div className="grid md:grid-cols-2 gap-8">
            <div className="h-96 bg-[#E5D9C5]/40 rounded-3xl animate-pulse" />
            <div className="h-96 bg-[#E5D9C5]/40 rounded-3xl animate-pulse" />
          </div>
        ) : grouped.map(section => (
          <div key={section.category}>
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <div className="chip">{section.category}</div>
                <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-[#2C241B]">
                  {section.category === "Plates" && "Choose your plate"}
                  {section.category === "Kelaguen" && "Chicken kelaguen"}
                  {section.category === "Lumpia" && "Crispy lumpia"}
                  {section.category === "Sides" && "Add extra sides"}
                </h2>
                {section.category === "Plates" && (
                  <p className="mt-2 text-[#7A6C5D] max-w-md">
                    Comes with BBQ ribs, red rice, potato salad, and pickles.
                  </p>
                )}
                {section.category === "Kelaguen" && (
                  <p className="mt-2 text-[#7A6C5D] max-w-md">
                    Fresh chopped chicken with lemon and hot pepper — a Chamoru favorite.
                  </p>
                )}
                {section.category === "Lumpia" && (
                  <p className="mt-2 text-[#7A6C5D] max-w-md">
                    Golden, crispy, and hand-rolled. Pork or beef — you pick.
                  </p>
                )}
                {section.category === "Sides" && (
                  <p className="mt-2 text-[#7A6C5D] max-w-md">
                    Add an extra scoop of rice — ginger, red, or white.
                  </p>
                )}
                {section.category === "Spam Musubi" && (
                  <p className="mt-2 text-[#7A6C5D] max-w-md">
                    Hand-pressed spam musubi. Pick your rice: ginger, red, or white.
                  </p>
                )}
              </div>
            </div>
            <div data-testid={`menu-grid-${section.category.toLowerCase()}`} className={(section.category === "Plates" || section.category === "Kelaguen")
              ? "grid md:grid-cols-2 gap-6 sm:gap-8 fade-in"
              : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 fade-in"}>
              {section.items.map(item => <MenuCard key={item.id} item={item} compact={section.category !== "Plates" && section.category !== "Kelaguen"} />)}
            </div>
          </div>
        ))}
      </section>

      {/* Info */}
      <section id="info" className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
          <InfoCard icon={<MapPin className="w-5 h-5" />} title="Pickup Location" body="10508 S 54th Lane, Laveen, AZ 85339" testid="info-location" />
          <InfoCard icon={<Clock className="w-5 h-5" />} title="Pickup Hours" body={pickupHoursText} testid="info-hours" />
          <InfoCard icon={<Phone className="w-5 h-5" />} title="Contact" body="Call or text (253) 249-9463" testid="info-contact" />
        </div>
      </section>

      {/* Sticky mobile cart CTA */}
      {totals.count > 0 && (
        <div className="fixed bottom-4 inset-x-4 sm:hidden z-30">
          <button
            data-testid="mobile-sticky-cart-btn"
            onClick={() => setIsOpen(true)}
            className="cta-primary w-full rounded-full py-3.5 font-display font-extrabold text-base flex items-center justify-center gap-2 shadow-2xl"
          >
            <ShoppingBag className="w-5 h-5" /> View Cart · {totals.count} · ${totals.total.toFixed(2)}
          </button>
        </div>
      )}

      <footer className="border-t border-[#E5D9C5] py-10 text-center text-sm text-[#7A6C5D]">
        <div className="flex items-center justify-center gap-3 mb-4">
          <a
            href="https://www.instagram.com/hafaadaikitchen"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="social-instagram-link"
            aria-label="Follow Hafa Adai Kitchen on Instagram"
            className="w-11 h-11 rounded-full bg-white border border-[#E5D9C5] flex items-center justify-center text-[#2C241B] hover:bg-[#D9381E] hover:text-white hover:border-[#D9381E] transition-colors"
          >
            <Instagram className="w-5 h-5" />
          </a>
          <a
            href="https://www.facebook.com/demetri.turpin.77"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="social-facebook-link"
            aria-label="Follow Hafa Adai Kitchen on Facebook"
            className="w-11 h-11 rounded-full bg-white border border-[#E5D9C5] flex items-center justify-center text-[#2C241B] hover:bg-[#D9381E] hover:text-white hover:border-[#D9381E] transition-colors"
          >
            <Facebook className="w-5 h-5" />
          </a>
        </div>
        <div className="font-semibold text-[#2C241B]">Follow us @hafaadaikitchen</div>
        <div className="mt-1">© {new Date().getFullYear()} Hafa Adai Kitchen · Laveen, AZ · (253) 249-9463</div>
      </footer>
    </div>
  );
}

function InfoCard({ icon, title, body, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl p-6 border border-[#E5D9C5] shadow-[0_6px_20px_rgba(44,36,27,0.05)]">
      <div className="w-10 h-10 rounded-xl bg-[#2C5E3B]/10 text-[#2C5E3B] flex items-center justify-center">{icon}</div>
      <div className="mt-3 font-display font-extrabold text-lg text-[#2C241B]">{title}</div>
      <div className="mt-1 text-sm text-[#7A6C5D]">{body}</div>
    </div>
  );
}
